/**
 * Landing Page View
 * Pure presentational component - receives all data and callbacks via props
 * NO business logic here - all logic is in the ViewModel
 */

import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { ColorModeButton } from "../../../components/ui/color-mode";
import { AppStoreIcon, PhoneIcon, LoginIcon } from "./components/Icons";
import { AssetImage } from "../../../components/AssetImage";
import type { LandingViewModel } from "../viewModel/useLandingViewModel";

interface LandingViewProps {
  viewModel: LandingViewModel;
}

export function LandingView({ viewModel }: LandingViewProps) {
  const {
    config,
    benefits,
    isLoading,
    isConfigLoading,
    error,
    platform,
    handlePlayStoreClick,
    handleAppStoreClick,
    handleLoginClick,
    clearError,
  } = viewModel;

  // Loading state - purely presentational
  if (isConfigLoading || isLoading) {
    return (
      <Box
        minH="100vh"
        bg="bg"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="4">
          <Spinner size="xl" color="accent" />
          <Text color="fg.muted">
            {isLoading ? "Logging in..." : "Loading..."}
          </Text>
        </VStack>
      </Box>
    );
  }

  // Error state - purely presentational
  if (error) {
    return (
      <Box minH="100vh" bg="bg">
        <Container maxW="md" py="10">
          <VStack gap="6" align="center" justify="center" minH="80vh">
            <Text fontSize="xl" color="fg">
              Login Failed
            </Text>
            <Text color="fg.muted" textAlign="center">
              {error}
            </Text>
            <Text color="accent" cursor="pointer" onClick={clearError}>
              Try again
            </Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  // Main landing view

  return (
    <Box
      h="100dvh"
      w="full"
      bg="bg"
      position="relative"
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {/* Background - hardcoded for both modes */}
      <Box
        position="absolute"
        inset="0"
        style={{
          background: "var(--landing-bg)",
        }}
      />

      {/* Animated Background Circles */}
      <Box
        position="absolute"
        top="-10%"
        right="-5%"
        w={{ base: "60vw", md: "40vw" }}
        h={{ base: "60vw", md: "40vw" }}
        minW={{ base: "200px", md: "400px" }}
        minH={{ base: "200px", md: "400px" }}
        borderRadius="full"
        style={{ background: "var(--bubble-top-color)" }}
        opacity={{ base: 0.45, _dark: 0.3 }}
        filter="blur(40px)"
        animation="float 4s ease-in-out infinite"
      />
      <Box
        position="absolute"
        bottom="-10%"
        left="-5%"
        w={{ base: "50vw", md: "30vw" }}
        h={{ base: "50vw", md: "30vw" }}
        minW={{ base: "150px", md: "300px" }}
        minH={{ base: "150px", md: "300px" }}
        borderRadius="full"
        style={{ background: "var(--bubble-bottom-color)" }}
        opacity={{ base: 0.45, _dark: 0.3 }}
        filter="blur(40px)"
        animation="floatReverse 4s ease-in-out infinite"
      />

      {/* Theme Toggle */}
      <Box position="absolute" top="4" right="4" zIndex="sticky">
        <ColorModeButton size="md" />
      </Box>

      <Container
        maxW="md"
        w="full"
        py={{ base: 4, md: "4vh", lg: "6vh" }}
        position="relative"
        zIndex="1"
      >
        <VStack
          gap={{ base: 4, md: "1.5vh", lg: "2vh" }}
          w="full"
          justify="center"
        >
          {/* Hero Section */}
          <HeroSection companyName={config.appName} logo={config.logo} />

          {/* Main Card */}
          <MainCard
            benefits={benefits}
            platform={platform}
            onPlayStoreClick={handlePlayStoreClick}
            onAppStoreClick={handleAppStoreClick}
            onLoginClick={handleLoginClick}
          />

          {/* Footer */}
          <Text color="fg" fontSize="10px" textAlign="center" opacity="0.8">
            © {new Date().getFullYear()} Neobhi. All rights reserved.
          </Text>
        </VStack>
      </Container>

      {/* CSS Keyframes + CSS Variables for both modes */}
      <style>
        {`
          :root {
            --landing-bg: linear-gradient(135deg, #dbeafe 0%, #f0f9ff 40%, #ede9fe 100%);
            --bubble-top-color: #60a5fa;
            --bubble-bottom-color: #f472b6;
            --card-bg: transparent;
            --card-border: rgba(255, 255, 255, 0.85);
            --card-shadow: 0 24px 48px rgba(99,102,241,0.08), inset 0 1px 0 rgba(255,255,255,0.8);
          }
          .dark {
            --landing-bg: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #09090b 100%);
            --bubble-top-color: #6366f1;
            --bubble-bottom-color: #a855f7;
            --card-bg: transparent;
            --card-border: rgba(99, 102, 241, 0.25);
            --card-shadow: 0 24px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06);
          }
          @keyframes float {
            0%, 100% { transform: scale(1); opacity: 0.45; }
            50% { transform: scale(1.08); opacity: 0.6; }
          }
          @keyframes floatReverse {
            0%, 100% { transform: scale(1); opacity: 0.45; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }
        `}
      </style>
    </Box>
  );
}

// Sub-components for better organization

interface HeroSectionProps {
  companyName: string;
  logo?: string;
}

function HeroSection({ companyName, logo }: HeroSectionProps) {
  return (
    <VStack gap={{ base: 2, md: "1vh" }} textAlign="center">
      {/* App Icon/Logo Container - Solid white to support any logo color in dark mode */}
      <Box
        p={{ base: 3, md: 4 }}
        bg="white"
        borderRadius="3xl" /* Extra rounded corners for the app-icon look */
        boxShadow="0 10px 25px rgba(0,0,0,0.1), 0 4px 10px rgba(0,0,0,0.05)"
        color="brand.600"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
        w={{ base: "4rem", md: "5rem" }}
        h={{ base: "4rem", md: "5rem" }}
      >
        {logo ? (
          <Box
            w="3.5rem"
            h="3.5rem"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <AssetImage assetId={logo} alt={companyName} />
          </Box>
        ) : (
          <PhoneIcon />
        )}
      </Box>

      <Heading
        as="h1"
        size={{ base: "xl", md: "2xl" }}
        color={{ base: "gray.800", _dark: "white" }}
        fontWeight="bold"
        letterSpacing="-0.02em"
      >
        {companyName}
      </Heading>
    </VStack>
  );
}

interface MainCardProps {
  benefits: { id: string; text: string; icon: string }[];
  platform: "android" | "ios" | "unknown";
  onPlayStoreClick: () => void;
  onAppStoreClick: () => void;
  onLoginClick: () => void;
}

function MainCard({
  benefits,
  platform,
  onPlayStoreClick,
  onAppStoreClick,
  onLoginClick,
}: MainCardProps) {
  const isIOS = platform === "ios";
  const isAndroid = platform === "android";
  const isDesktop = platform === "unknown";

  return (
    <Box
      w="full"
      style={{
        background: "transparent",
        border: "1px solid var(--card-border)",
        boxShadow: "var(--card-shadow)",
      }}
      borderRadius="3xl"
      p={{ base: 5, sm: 6, md: 8 }}
      backdropFilter="blur(24px)"
    >
      <VStack gap={{ base: 4, md: 6 }}>
        {/* App Benefits Header */}
        <VStack gap="2" textAlign="center">
          <Heading
            as="h2"
            size={{ base: "md", md: "lg" }}
            color="fg"
            fontWeight="semibold"
          >
            Get the App
          </Heading>
          <Text color="fg.muted" fontSize={{ base: "sm", md: "md" }}>
            Download our app for the best experience:
          </Text>
        </VStack>

        {/* Benefits List */}
        <VStack
          gap="3"
          align="start"
          w="full"
          px={{ base: 2, sm: 4 }}
          display={{ base: "none", sm: "flex" }}
        >
          {Array.isArray(benefits) &&
            benefits.map((benefit) => (
              <Text
                key={benefit.id}
                color="fg"
                fontSize="sm"
                display="flex"
                alignItems="center"
                gap="2"
              >
                <Box
                  as="span"
                  color="green.500"
                  display="flex"
                  alignItems="center"
                >
                  {benefit.icon}
                </Box>
                {benefit.text}
              </Text>
            ))}
        </VStack>

        <Box w="full" display="flex" flexDirection="column" gap={3} mt={2}>
          {(isAndroid || isDesktop) && (
            <Button
              size="lg"
              w="full"
              flex="1"
              h="auto"
              py={{ base: 4, md: 5 }}
              px={{ base: 4, md: 4 }}
              bg="black"
              color="white"
              borderRadius="xl"
              onClick={onPlayStoreClick}
              _hover={{
                bg: "gray.900",
                transform: "translateY(-2px)",
                boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
              }}
              transition="all 0.3s ease"
              gap={{ base: 3, md: 4 }}
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <img
                src="/gplay.svg"
                alt="Google Play"
                style={{ width: "36px", height: "auto" }}
              />
              <VStack gap="0" align="start" w="145px">
                <Text
                  fontSize={{ base: "11px", md: "12px" }}
                  fontWeight="bold"
                  opacity="0.9"
                  textTransform="uppercase"
                  lineHeight="1"
                  mb="2px"
                >
                  GET IT ON
                </Text>
                <Text
                  fontSize={{ base: "2xl", md: "2xl" }}
                  fontWeight="extrabold"
                  lineHeight="1"
                >
                  Google Play
                </Text>
              </VStack>
            </Button>
          )}

          {(isIOS || isDesktop) && (
            <Button
              size="lg"
              w="full"
              flex="1"
              h="auto"
              py={{ base: 4, md: 5 }}
              px={{ base: 4, md: 4 }}
              bg="black"
              color="white"
              borderRadius="xl"
              onClick={onAppStoreClick}
              _hover={{
                bg: "gray.900",
                transform: "translateY(-2px)",
                boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
              }}
              transition="all 0.3s ease"
              gap={{ base: 3, md: 4 }}
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Box
                w="36px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box transform="scale(2.5)">
                  <AppStoreIcon />
                </Box>
              </Box>
              <VStack gap="0" align="start" w="145px">
                <Text
                  fontSize={{ base: "13px", md: "14px" }}
                  fontWeight="bold"
                  opacity="0.9"
                  lineHeight="1"
                  mb="2px"
                >
                  Download on the
                </Text>
                <Text
                  fontSize={{ base: "2xl", md: "2xl" }}
                  fontWeight="extrabold"
                  lineHeight="1"
                >
                  App Store
                </Text>
              </VStack>
            </Button>
          )}
        </Box>

        {/* Divider */}
        <Box w="full" display="flex" alignItems="center" gap="4" py="1">
          <Box
            flex="1"
            h="1px"
            bg={{ base: "blackAlpha.200", _dark: "whiteAlpha.200" }}
          />
          <Text color="fg.muted" fontSize="sm">
            or
          </Text>
          <Box
            flex="1"
            h="1px"
            bg={{ base: "blackAlpha.200", _dark: "whiteAlpha.200" }}
          />
        </Box>

        {/* Web Login Button */}
        <Button
          size="lg"
          w="full"
          h={{ base: "3.5rem", md: "3.5rem" }}
          py={5}
          variant="outline"
          bg="transparent"
          color="fg"
          borderRadius="xl"
          borderColor={{ base: "blackAlpha.300", _dark: "whiteAlpha.300" }}
          fontSize="md"
          fontWeight="medium"
          onClick={onLoginClick}
          _hover={{
            bg: { base: "blackAlpha.50", _dark: "whiteAlpha.100" },
            borderColor: { base: "blackAlpha.500", _dark: "whiteAlpha.500" },
          }}
          transition="all 0.3s ease"
          gap="2"
        >
          <LoginIcon />
          Continue in Browser
        </Button>
      </VStack>
    </Box>
  );
}
